import { ComboBoxItem } from "../../Shared/Components/Forms/ComboBox";
import { Frequency, rrulestr, RRule } from "rrule";
import {
  FrequencyTypes,
  ByWeekday,
  ByMonthDay1,
  ByMonthDay2,
  ByMonthDay3,
  ByMonthDay4,
} from "../../../Constants/SchedulerConstants";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../messages/en-US";
import { RequirementReadDto } from "../../../Models/Requirement/RequirementReadDto";

export const greasePatternsToComboboxItems = (existingGreasePatterns: Array<RequirementReadDto>) => {
  return existingGreasePatterns
    ?.filter((data) => {
      return data.id;
    })
    .map((pattern) => {
      return {
        displayName: pattern.name,
        value: pattern.id,
      };
    }) as Array<ComboBoxItem>;
};

export const greasePatternToComboboxItem = (patternId: string, existingGreasePatterns: Array<RequirementReadDto>) => {
  return patternId != null
    ? ({
        displayName: existingGreasePatterns?.find((p) => p?.id === patternId)?.name,
        value: patternId,
      } as ComboBoxItem)
    : null;
};

export const rruleFrequencyToText = (rRule: string | undefined | null) => {
  // Handle undefined, null, or empty string
  if (!rRule || rRule.trim() === '') {
    return 'Per Pump Cycle';
  }

  // Check if this is a pump cycle by looking for the custom identifier
  if (rRule.includes('PUMPCYCLE=TRUE')) {
    return 'Per Pump Cycle';
  }

  // Remove RRULE: prefix if it exists
  const rRuleString = rRule.replace('RRULE:', '');

  try {
    const rule = rrulestr(rRuleString);
    switch (rule.origOptions.freq) {
      case Frequency.MONTHLY:
        return 'Monthly';
      case Frequency.WEEKLY:
        return 'Weekly';
      case Frequency.DAILY:
        return 'Daily';
      case Frequency.SECONDLY:
        return 'Fixed Interval';
      default:
        return '';
    }
  } catch (error) {
    console.warn('Error parsing rRule:', rRule, error);
    return '';
  }
};

export const rruleFrequencyToTextRRule = (rRule: string) => {
  // Check if this is a pump cycle (empty rRule or PUMP_CYCLE identifier)
  if (!rRule || rRule.trim() === '' || rRule === 'PUMP_CYCLE') {
    return ""; // Return empty string for pump cycle
  }
  
  let rule = rrulestr(rRule);
  if (rule.origOptions.freq === Frequency.MONTHLY) {
    return "RRule.MONTHLY";
  } else if (rule.origOptions.freq === Frequency.WEEKLY) {
    return "RRule.WEEKLY";
  } else if (rule.origOptions.freq === Frequency.DAILY) {
    return "RRule.DAILY";
  } else if (rule.origOptions.freq === Frequency.SECONDLY) {
    return "RRule.SECONDLY";
  } else return "";
};

export const rruleRecurrenceToText = (rRule: string | undefined | null) => {
  // Handle undefined, null, or empty string
  if (!rRule || rRule.trim() === '') {
    return 'Every Pump Cycle';
  }

  // Check if this is a pump cycle by looking for the custom identifier
  if (rRule.includes('PUMPCYCLE=TRUE')) {
    return 'Every Pump Cycle';
  }

  // Remove RRULE: prefix if it exists
  const rRuleString = rRule.replace('RRULE:', '');

  try {
    const rule = rrulestr(rRuleString);
    switch (rule.origOptions.freq) {
      case Frequency.MONTHLY:
        return `Every ${rule.origOptions.interval ?? 1} Months`;
      case Frequency.WEEKLY:
        return `Every ${rule.origOptions.interval ?? 1} Weeks`;
      case Frequency.DAILY:
        return `Every ${rule.origOptions.interval ?? 1} Days`;
      case Frequency.SECONDLY:
        return `Every ${rule.origOptions.interval ?? 1} Seconds`;
      default:
        return '';
    }
  } catch (error) {
    console.warn('Error parsing rRule:', rRule, error);
    return '';
  }
};

export const rruleSummaryToText = (rRule: string | undefined | null) => {
  // Handle undefined, null, or empty string
  if (!rRule || rRule.trim() === '') {
    return 'Every Pump Cycle';
  }

  // Check if this is a pump cycle by looking for the custom identifier
  if (rRule.includes('PUMPCYCLE=TRUE')) {
    return 'Every Pump Cycle';
  }

  // Remove RRULE: prefix if it exists
  const rRuleString = rRule.replace('RRULE:', '');

  try {
    const rule = rrulestr(rRuleString);
    switch (rule.origOptions.freq) {
      case Frequency.MONTHLY:
        return `Every ${rule.origOptions.interval ?? 1} Month(s)`;
      case Frequency.WEEKLY:
        return `Every ${rule.origOptions.interval ?? 1} Week(s)`;
      case Frequency.DAILY:
        return `Every ${rule.origOptions.interval ?? 1} Day(s)`;
      case Frequency.SECONDLY:
        return `Every ${rule.origOptions.interval ?? 1} Second(s)`;
      default:
        return '';
    }
  } catch (error) {
    console.warn('Error parsing rRule:', rRule, error);
    return '';
  }
};

/**
 * This method returns a valid start date based on the frequency of a schedule if weekly, get start of week, if Monthly, get start of month
 * @param date The suggested start date
 * @param frequency The frequency of the RRULE for the schedule
 * @returns a valid start date
 */
export const getValidStartDateForFrequency = (date: Date | null | undefined, frequency: string) => {
  if (frequency === FrequencyTypes.weekly && date != null && date !== undefined) {
    let day = date!.getDay();
    let diff = date!.getDate() - day;
    date = new Date(date.setDate(diff));
  } else if (frequency === FrequencyTypes.monthly && date != null && date !== undefined) {
    date = new Date();
    const y = date.getFullYear();
    const m = date.getMonth();
    date = new Date(y, m, 1);
  }
  return date;
};

export const minEndDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);

  return date;
};

export const FrequencyStringToDisplayName = (frequency: string) => {
  const localizationService = useLocalization();
  if (frequency === "RRule.DAILY") {
    return `${localizationService.toLanguageString('custom.daily', enMessages.custom.daily)}`;
  }
  if (frequency === "RRule.WEEKLY") {
    return `${localizationService.toLanguageString('custom.weekly', enMessages.custom.weekly)}`;
  }
  if (frequency === "RRule.MONTHLY") {
    return `${localizationService.toLanguageString('custom.monthly', enMessages.custom.monthly)}`;
  }
  if (frequency === "RRule.SECONDLY") {
    return `${localizationService.toLanguageString('custom.pumpCycle', enMessages.custom.pumpCycle)}`;
  }
  if (frequency === "RRule.None") {
    return `${localizationService.toLanguageString('custom.pumpCycle', enMessages.custom.pumpCycle)}`;
  }
  return "";
};

export const rRuleGenerator = (
  recurrence: number,
  startDate: Date | null | undefined,
  endDate: Date | null | undefined,
  frequency: string,
  daysSelected: Array<number>,
  week1Selected: Array<number>,
  week2Selected: Array<number>,
  week3Selected: Array<number>,
  week4Selected: Array<number>
) => {
  let rule = new RRule({});

  if (frequency === FrequencyTypes.daily) {
    rule = new RRule({
      freq: RRule.DAILY,
      interval: recurrence,
      dtstart: startDate,
      until: endDate,
    });
  } else if (frequency === FrequencyTypes.weekly) {
    let daySelect = daysSelected.sort().map((i: number) => ByWeekday[i]);
    rule = new RRule({
      freq: RRule.WEEKLY,
      interval: recurrence,
      byweekday: daySelect,
      dtstart: startDate,
      until: endDate,
    });
  } else if (frequency === FrequencyTypes.monthly) {
    let daySelect1 = week1Selected.sort().map((i: number) => ByMonthDay1[i]);
    let daySelect2 = week2Selected.sort().map((i: number) => ByMonthDay2[i]);
    let daySelect3 = week3Selected.sort().map((i: number) => ByMonthDay3[i]);
    let daySelect4 = week4Selected.sort().map((i: number) => ByMonthDay4[i]);
    let daySelectMonth = daySelect1.concat(daySelect2, daySelect3, daySelect4);
    rule = new RRule({
      freq: RRule.MONTHLY,
      interval: recurrence,
      byweekday: daySelectMonth,
      dtstart: startDate,
      until: endDate,
    });
  } else if (frequency === FrequencyTypes.secondly) {
    rule = new RRule({
      freq: RRule.SECONDLY,
      interval: recurrence,
      dtstart: startDate,
      until: endDate,
    });
  } else if (frequency === FrequencyTypes.pumpCycle) {
    // For pump cycle, return null (no RRULE)
    return null;
  }

  return rule;
};
